import { GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { process, State, GroupDescriptor, SortDescriptor } from '@progress/kendo-data-query';
import { ColumnSettings } from './column-settings';
import { GridEventHandler } from './grid-settings';

export class GridViewManager
{
    /**
     * Indicate if the grid is loading data. This is being use to control the show/hide of the grid loading indicator.
     */
    public loading: boolean;

    /**
     * Contains the grid state.
     */
    public state: State;

    /**
     * Contains the grid data in JSON format.
     */
    public data: any[];

    /**
     * Contains the Kendo grid data result object.
     */
    public gridData: GridDataResult;

    /**
     * Contains the Kendo groups used for the group by.
     */
    public groups: GroupDescriptor[];

    /**
     * Optional Column Config to persist grid state
     */
    public columnsConfig: ColumnSettings[];

    /**
     * Creates an instance of GridViewManager.
     */
    constructor(pageSize = 5)
    {
        this.state = {
            skip: 0,
            group: [],
            sort: [],
            take: pageSize
        };
        this.loading = false;
        this.groups = [];
        this.data = [];
    }

    /**
     * Start loading the grid and display the loading indicator.
     */
    public startLoading(): void { this.setLoading(true); }

    /**
     * Stop loading the grid and hide the loading indicator.
     */
    public stopLoading(): void { this.setLoading(false); }

    /**
     * Applies grid DataStateChangeEvent by copying onto the existing State object.
     *
     * Replacing `this.state` with the event object breaks bindings such as `[filter]="view.state.filter"`:
     * the grid and parent can disagree on object identity, so filter UI can show stale values.
     * Mutating this.state in place keeps one State instance for the lifetime of the view.
     *
     * @param state Set grid state being used on the dataStateChange event.
     */
    public setState(state: DataStateChangeEvent): void
    {
        const incoming = state as State;
        this.state.skip = incoming.skip;
        this.state.take = incoming.take;
        this.state.sort = incoming.sort ?? [];
        this.state.filter = incoming.filter;
        if (incoming.group !== undefined)
        {
            this.state.group = incoming.group;
            this.groups = incoming.group;
        }
        this.refreshView();
    }

    /**
     * @param any data Set grid data.
     */
    public setData(data: any): void
    {
        this.state.skip = 0;
        this.data = data;
        this.refreshView();
    }

    /**
     * @param GroupDescriptor[] group - Set the group option for the grid view.
     * Example: [{ field: 'organization' }, { field: 'department' }]
     */
    public setGroup(group: GroupDescriptor[]): void
    {
        this.groups = group;
        this.state.group = group;
        this.refreshView();
    }

    /**
     * @param SortDescriptor sort - Set the sort option for the grid view.
     * Example: [ { field: 'name',  dir: 'asc' }]
     */
    public setSort(sort: SortDescriptor[]): void
    {
        this.state.sort = sort;
        this.refreshView();
    }

    /**
     * Refresh the grid data.
     */
    public refreshView(): void
    {
        this.gridData = process(this.data, this.state);
    }

    /**
     * Add or update the grid row with new data.
     */
    public addOrUpdateGridRow(item: any): void
    {
        const index = this.data.findIndex(x => x.id === item.id);

        if (index === -1)
        {
            this.data.unshift(item); // add to the front
        }
        else
        {
            this.data[index] = item; // update item
        }

        this.refreshView();
    }

    /**
     * Add multiple rows to the grid data.
     *
     * @param items New items to add.
     */
    public addRows(items: any[]): void
    {
        this.data = items.concat(this.data);
        this.refreshView();
    }

    /**
     * Add multiple rows to the grid data.
     * If the item already exists, updates its values.
     *
     * @param items Items to add or update.
     * @param equals Predicate function to test equality.
     */
    public addOrUpdateRows(items: any[], equals: (x: any, y: any) => boolean): void
    {
        items.forEach(item =>
        {
            let existing = this.data.find(x => equals(x, item));

            if (existing)
            {
                existing = item; // update item
            }
            else
            {
                this.data.unshift(item); // add to the front
            }
        });

        this.refreshView();
    }

    /**
     * Removes an item from the data grid (by ID match).
     *
     * @param item Item to remove.
     */
    public removeRow(item: any): void
    {
        this.data = this.data.filter(x => x.id !== item.id);
        this.refreshView();
    }

    /**
     * Save event handler for in-grid editing.
     */
    public saveHandler(event: GridEventHandler): void
    {
        event.dataItem.editing = false;
        event.sender.closeRow(event.rowIndex);
        this.addOrUpdateGridRow(event.dataItem);
    }

    /**
     * Cancel event handler for in-grid editing.
     */
    public cancelHandler(event: GridEventHandler): void
    {
        event.sender.closeRow(event.rowIndex);
    }

    /**
     * Set loading true/false.
     *
     * @param boolean value the loading value to set.
     */
    private setLoading(value: boolean): void
    {
        this.loading = value;
    }
}
